import { useState, type FormEvent } from 'react';
import type { CardRow } from '@shared/domain/card.ts';
import { api } from '../lib/api.ts';
import { friendlyMessage } from '../lib/errors.ts';
import { formatInstant } from '../lib/format.ts';
import { useApp } from '../state/store.ts';
import { TagChip } from '../components/common.tsx';

/** Búsqueda por nombre de tarjetas cerradas hace más de 60 días (consulta a Notion, sin guardar). */
export function SearchPage() {
  const { online } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CardRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function search(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResults(await api.searchClosed(query));
    } catch (err) {
      setError(friendlyMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Buscar tarjetas cerradas</h1>
      <p className="muted">
        La lista muestra las cerradas de los últimos 60 días. Aquí puedes buscar las más antiguas
        por nombre; los resultados no se guardan.
      </p>
      <form className="row" onSubmit={(e) => void search(e)}>
        <label className="grow">
          Nombre
          <input
            type="search"
            minLength={2}
            required
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <button type="submit" className="button button-primary" disabled={busy || !online}>
          Buscar
        </button>
      </form>
      {!online && <p className="banner banner-offline">La búsqueda necesita conexión.</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {results && results.length === 0 && (
        <p className="empty">No hay cerradas antiguas con ese nombre.</p>
      )}
      {results && results.length > 0 && (
        <ul className="card-list">
          {results.map((c) => (
            <li key={c.id} className="card-item">
              <a
                href={c.notion_url}
                target="_blank"
                rel="noreferrer noopener"
                className="card-title"
              >
                {c.name}
              </a>
              <span className="card-meta">
                {c.etiquetas.map((t) => (
                  <TagChip key={t} tag={t} />
                ))}
                {c.marca && <span className="chip chip-brand">{c.marca}</span>}
              </span>
              <span className="muted small">
                Última edición: {formatInstant(c.notion_last_edited)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
